import type { ReactNode } from "react";
import { LockClosedIcon } from "@heroicons/react/24/outline";

import { useAuth } from "@/features/auth/state/useAuth";
import type { PermissionCode } from "@/features/auth/permissions";
import type { AdminRole } from "@/features/auth/types";

type PermissionGateProps = {
    children: ReactNode;
    anyOf?: readonly PermissionCode[];
    allOf?: readonly PermissionCode[];
    roles?: readonly AdminRole[];
    fallback?: ReactNode;
};

export const AccessDeniedState = ({
    title = "Access Restricted",
    description = "You do not have permission to view this area with your current admin account.",
}: {
    title?: string;
    description?: string;
}) => (
    <div className="flex min-h-[320px] items-center justify-center">
        <div className="max-w-lg rounded-3xl border border-slate-200 bg-white px-8 py-10 text-center shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-100 dark:bg-amber-900/30">
                <LockClosedIcon className="h-7 w-7 text-amber-600 dark:text-amber-400" />
            </div>
            <h1 className="mt-5 text-xl font-semibold text-slate-900 dark:text-white">
                {title}
            </h1>
            <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                {description}
            </p>
        </div>
    </div>
);

export const PermissionGate = ({
    children,
    anyOf,
    allOf,
    roles,
    fallback = null,
}: PermissionGateProps) => {
    const { user, hasAnyPermission, hasAllPermissions } = useAuth();

    const meetsRoleRequirement =
        !roles || roles.length === 0 || (user?.role ? roles.includes(user.role) : false);

    const meetsAnyRequirement = !anyOf || anyOf.length === 0 || hasAnyPermission(anyOf);
    const meetsAllRequirement = !allOf || allOf.length === 0 || hasAllPermissions(allOf);

    if (meetsRoleRequirement && meetsAnyRequirement && meetsAllRequirement) {
        return <>{children}</>;
    }

    return <>{fallback}</>;
};

export const PermissionRoute = ({
    children,
    anyOf,
    allOf,
    roles,
    fallback,
}: PermissionGateProps) => (
    <PermissionGate
        anyOf={anyOf}
        allOf={allOf}
        roles={roles}
        fallback={fallback ?? <AccessDeniedState />}
    >
        {children}
    </PermissionGate>
);
